import {
  action,
  thunk,
  computed,
  thunkOn,
  type Action,
  type Thunk,
  type Computed,
  type ThunkOn,
} from 'easy-peasy'
import { v4 as uuid } from 'uuid'
import type { FormStepResult, FMPStoreModel } from '../../types'
import { dynamicProgressCalculator } from '../../utils/dynamicProgressCalculator'
import { isStepVisible } from '../../utils/countVisibleSteps'
import saveToLocalStorage from '../../utils/saveToLocalStorage'
import sanitizeData from '@strata-monorepo/utils/sanitizeData'
import {
  LEAD_FORM_SENSITIVE_FIELDS,
  ENRICH_CUSTOMER_SENSITIVE_FIELDS,
} from '@strata-monorepo/utils/constants/piiSensitiveData'
import {
  getProgressDisplayConfig,
  type ProgressDisplayConfig,
} from '../../constants/progressDisplayConfig'
import { FMP_FORM_NAME } from '../../constants/questionnaires/default'
import type { FormFlow, FmpFormStep } from '@strata-monorepo/find-my-plan/types/form'
import {
  questionnaireMap,
  type MonarchQuestionnaireName,
} from '../../constants/questionnaires/questionnaireMap'
import { QuestionFlowEnum, FormTypeEnum } from '@strata-monorepo/shared-types'

export interface FormModel {
  // states
  formVersion: string
  formId: string
  formResults: FormStepResult
  isFormCompleted: boolean
  initialStepIndex: number
  currentStepIndex: number
  currentStep: FmpFormStep | null
  lastCompletedStepName: string
  quizCompletionPercentage: number
  filteredQuestions: FmpFormStep[]
  checkpointProgress: number
  progressHistoryStack: number[]
  currentStepNumber?: number
  totalSteps?: number
  monarchQuestionnaireName: MonarchQuestionnaireName | null
  /**
   * True when the user entered the quiz with a zipcode already populated (post-ZIP modal entry).
   * False when the user entered without a zipcode (direct FMP entry).
   * Used to determine "Take Me to Plans" button visibility behavior.
   */
  isPostZipEntry: boolean

  // Computed
  apiRequestIsLoading: Computed<FormModel, boolean, FMPStoreModel>
  isDeepDiveFlow: Computed<FormModel, boolean, FMPStoreModel>
  /**
   * Returns the active form's type string (variant) for the current questionnaire.
   * For questionnaires with explicit formType, returns that value directly.
   * For the default questionnaire, derives from the interstitial answer.
   */
  formType: Computed<FormModel, FormTypeEnum, FMPStoreModel>
  /**
   * UI/progress config for the active flow (e.g. quick vs deep dive).
   * Sourced from `progressDisplayConfig` so adding a new flow is a one-line change
   * there — no UI component or store branching required.
   */
  progressDisplay: Computed<FormModel, ProgressDisplayConfig, FMPStoreModel>
  isSkipToResultsEnabled: Computed<FormModel, boolean, FMPStoreModel>
  filterQuestions: Computed<FormModel, (bannedQuestions: string[]) => FmpFormStep[], FMPStoreModel>
  questionnaire: Computed<FormModel, FormFlow | null, FMPStoreModel>

  // actions
  setCurrentStep: Action<FormModel, FmpFormStep | null>
  setFormResults: Action<FormModel, FormStepResult>
  setIsFormCompleted: Action<FormModel, boolean>
  setCurrentStepIndex: Action<FormModel, number>
  setLastCompletedStepName: Action<FormModel, string>
  setQuizCompletionPercentage: Action<FormModel, number>
  setFilteredQuestions: Action<FormModel, FmpFormStep[]>
  setDynamicProgress: Action<
    FormModel,
    { currentStepIndex: number; mode?: 'step' | 'percentage'; updateStepCounters?: boolean }
  >
  /**
   * Clears the monotonic progress state (`checkpointProgress` and
   * `progressHistoryStack`). Called when committing an interstitial answer so
   * the high-water mark from a prior flow doesn't pin the bar above the new
   * flow's per-step percentage. See `handleNextStep`.
   */
  resetCheckpointProgress: Action<FormModel, void>
  restorePreviousProgress: Action<FormModel, void>
  setMonarchQuestionnaireName: Action<FormModel, MonarchQuestionnaireName | null>

  // listeners
  onCurrentStepChange: ThunkOn<FormModel, void, FMPStoreModel>

  // thunks
  handleFormCompletion: Thunk<FormModel, FormStepResult, void, FMPStoreModel, Promise<void>>

  handleErrorCta: Thunk<FormModel, void, void, FMPStoreModel>

  handleNextStep: Thunk<
    FormModel,
    { stepName: string; results: FormStepResult },
    void,
    FMPStoreModel,
    void
  >

  handlePrevStep: Thunk<
    FormModel,
    { stepName: string; results: FormStepResult },
    void,
    FMPStoreModel,
    void
  >
}

type DynamicProgressMode = 'step' | 'percentage'

interface ApplyDynamicProgressOptions {
  currentStepIndex: number
  mode: DynamicProgressMode
  questions: FmpFormStep[]
  updateStepCounters?: boolean
}

interface DynamicProgressState {
  formResults: FormStepResult
  checkpointProgress: number
  progressHistoryStack: number[]
  currentStepNumber?: number
  totalSteps?: number
}

const applyDynamicProgress = (
  state: DynamicProgressState,
  { currentStepIndex, mode, questions, updateStepCounters = false }: ApplyDynamicProgressOptions,
): void => {
  const calculator = new dynamicProgressCalculator()
  const result = calculator.calculate({
    mode,
    currentStepIndex,
    questions,
    formResults: state.formResults,
  })

  // Monotonicity guarantee: bar never moves backward even if core-if conditions
  // change mid-session and transiently reduce the active core count. Flow switches
  // (quick <-> deepDive) are handled separately by `resetCheckpointProgress` in
  // `handleNextStep` since they invalidate the high-water mark wholesale.
  const monotonicProgress = Math.max(state.checkpointProgress, result.progressPercentage)
  if (monotonicProgress > state.checkpointProgress) {
    state.progressHistoryStack.push(monotonicProgress)
  }
  state.checkpointProgress = monotonicProgress

  if (updateStepCounters) {
    state.currentStepNumber = result.currentStepNumber
    state.totalSteps = result.totalSteps
  }
}

export const formModel = (initialState: FormStepResult = {}, formId?: string): FormModel => {
  const resolvedFormId = formId ?? `fmp_${uuid()}`

  const address = initialState.address as Record<string, unknown> | undefined
  const hasZipcodeOnMount = Boolean(address?.zipcode)

  return {
    formVersion: FMP_FORM_NAME,
    formId: resolvedFormId,
    formResults: initialState,
    isFormCompleted: false,
    initialStepIndex: initialState.interstitial ? 1 : 0,
    currentStepIndex: initialState.interstitial ? 1 : 0,
    currentStep: null,
    lastCompletedStepName: '',
    quizCompletionPercentage: 0,
    filteredQuestions: [],
    checkpointProgress: 0,
    progressHistoryStack: [0],
    currentStepNumber: undefined,
    totalSteps: undefined,
    monarchQuestionnaireName: null,
    isPostZipEntry: hasZipcodeOnMount,

    apiRequestIsLoading: computed(
      [(_, storeState): boolean => storeState.enrichCustomer.enrichCustomerLoading],
      (enrichCustomerLoading): boolean => {
        return enrichCustomerLoading
      },
    ),

    isDeepDiveFlow: computed(
      [
        (state): FormStepResult => state.formResults,
        (state): FormFlow | null => state.questionnaire,
      ],
      (formResults, questionnaire): boolean => {
        if (questionnaire?.formType) {
          return questionnaire.formType === FormTypeEnum.LONG_FORM
        }
        // Fallback for the default questionnaire which has no explicit formType
        return formResults.interstitial === QuestionFlowEnum.DEEP_DIVE
      },
    ),

    formType: computed(
      [
        (state): FormStepResult => state.formResults,
        (state): FormFlow | null => state.questionnaire,
      ],
      (formResults, questionnaire): FormTypeEnum => {
        if (questionnaire?.formType) {
          return questionnaire.formType
        }
        return formResults.interstitial === QuestionFlowEnum.DEEP_DIVE
          ? FormTypeEnum.LONG_FORM
          : FormTypeEnum.SHORT_FORM
      },
    ),

    progressDisplay: computed(
      [
        (state): FormStepResult => state.formResults,
        (state): FormFlow | null => state.questionnaire,
      ],
      (formResults, questionnaire): ProgressDisplayConfig => {
        // If questionnaire has explicit progressDisplay, use it directly
        if (questionnaire?.progressDisplay) {
          return questionnaire.progressDisplay
        }
        // Fallback to interstitial-based logic for default questionnaire
        return getProgressDisplayConfig(formResults.interstitial)
      },
    ),

    /**
     * Determines whether the "Skip to Results" header button should be shown.
     *
     * Post-ZIP modal entry (isPostZipEntry = true): button is always visible.
     * Direct FMP entry (isPostZipEntry = false): button is hidden until the user has
     * submitted an address (zipcode present in formResults), then visible for all subsequent steps.
     */
    isSkipToResultsEnabled: computed(
      [(state): boolean => state.isPostZipEntry, (state): FormStepResult => state.formResults],
      (isPostZipEntry, formResults): boolean => {
        if (isPostZipEntry) {
          return true
        }

        const address = formResults.address as Record<string, unknown> | undefined
        return Boolean(address?.zipcode)
      },
    ),

    filterQuestions: computed(
      [(state): FormFlow | null => state.questionnaire],
      (questionnaire): ((bannedQuestions: string[]) => FmpFormStep[]) =>
        (bannedQuestions: string[]): FmpFormStep[] => {
          // Return empty array if questionnaire is not yet loaded
          if (!questionnaire) return []

          const questions: FmpFormStep[] = questionnaire.questions

          const filteredQuestions: FmpFormStep[] =
            !bannedQuestions || !bannedQuestions.length
              ? questions
              : questions.filter((question) => {
                  return !bannedQuestions.includes(question.name)
                })

          const lastQuestion = filteredQuestions.at(-1)
          if (lastQuestion) {
            lastQuestion.componentProps = {
              ...lastQuestion.componentProps,
              lastQuestion: true,
            }
          }

          return filteredQuestions
        },
    ),

    questionnaire: computed(
      [
        (state): MonarchQuestionnaireName | null => state.monarchQuestionnaireName,
        (state): FormStepResult => state.formResults,
      ],
      (monarchQuestionnaireName, initialFormResults): FormFlow | null => {
        if (!monarchQuestionnaireName) return null
        const questionnaireFactory = questionnaireMap[monarchQuestionnaireName]
        if (!questionnaireFactory) return null
        return questionnaireFactory(initialFormResults)
      },
    ),

    setCurrentStep: action((state, payload) => {
      state.currentStep = payload
    }),

    onCurrentStepChange: thunkOn(
      (actions) => actions.setCurrentStep,
      (actions, target, { getState }) => {
        const currentStep = target.payload
        if (!currentStep?.name) return

        const currentState = getState()

        // CRITICAL FIX: Do NOT recalculate step index based on filteredQuestions
        // This prevents race conditions where handleNextStep sets the correct index,
        // but onCurrentStepChange recalculates it to a different value when
        // filteredQuestions changes (e.g., when Monarch updates the questionnaire)
        // Trust handleNextStep's calculation and only update progress
        actions.setDynamicProgress({
          mode: currentState.progressDisplay.mode,
          currentStepIndex: currentState.currentStepIndex,
          updateStepCounters: true,
        })
      },
    ),

    restorePreviousProgress: action((state) => {
      if (state.progressHistoryStack.length > 1) {
        state.progressHistoryStack.pop()
        const previousProgress = state.progressHistoryStack[state.progressHistoryStack.length - 1]
        state.checkpointProgress = previousProgress ?? 0
      }
    }),

    setFormResults: action((state, payload) => {
      const preservedInterstitial = state.formResults.interstitial

      // Preserve interstitial value unless explicitly provided in payload
      // This prevents navigation from accidentally clearing flow-defining state
      const mergedResults = {
        ...state.formResults,
        ...payload,
      }

      // Explicitly restore interstitial if it should be preserved
      if (payload.interstitial === undefined && preservedInterstitial !== undefined) {
        mergedResults.interstitial = preservedInterstitial
      }

      state.formResults = mergedResults
    }),

    setIsFormCompleted: action((state, payload) => {
      state.isFormCompleted = payload
    }),

    setCurrentStepIndex: action((state, payload) => {
      state.currentStepIndex = payload
    }),

    setLastCompletedStepName: action((state, payload) => {
      state.lastCompletedStepName = payload
    }),

    setQuizCompletionPercentage: action((state, payload) => {
      state.quizCompletionPercentage = payload
    }),

    setFilteredQuestions: action((state, payload) => {
      state.filteredQuestions = payload
    }),

    resetCheckpointProgress: action((state) => {
      state.checkpointProgress = 0
      state.progressHistoryStack = [0]
    }),

    setDynamicProgress: action((state, payload) => {
      const { currentStepIndex, mode = 'percentage', updateStepCounters = false } = payload

      applyDynamicProgress(state, {
        mode,
        currentStepIndex,
        questions: state.filteredQuestions,
        updateStepCounters,
      })
    }),

    setMonarchQuestionnaireName: action((state, payload) => {
      state.monarchQuestionnaireName = payload
      if (payload) {
        const questionnaireFactory = questionnaireMap[payload]
        if (questionnaireFactory) {
          const questionnaire = questionnaireFactory(state.formResults)
          state.formVersion = questionnaire.version

          // Apply initialState only for non-default questionnaires
          // Default questionnaire handles interstitial via constructor initialState
          if (payload !== 'default' && questionnaire.initialState) {
            state.formResults = {
              ...state.formResults,
              ...questionnaire.initialState,
            }
          }
        }
      }
    }),

    handleFormCompletion: thunk(async (actions, results, { getStoreState }) => {
      const storeState = getStoreState()
      const environment = storeState.env.environment
      const formId = storeState.form.formId

      actions.setFormResults(results)
      actions.setIsFormCompleted(true)

      // Read the final progress that the user actually saw. checkpointProgress is
      // already the authoritative, monotonic value maintained by setCurrentStep.
      const finalProgress = getStoreState().form.checkpointProgress
      actions.setQuizCompletionPercentage(finalProgress)

      // Get data from other models for localStorage
      const state = getStoreState()
      const enrichCustomerResponse = state.enrichCustomer.enrichCustomerResponse
      const consumptionPredictionResponse =
        state.consumptionPrediction.consumptionPredictionResponse

      // Add formId to results before saving
      const resultsWithFormId = {
        ...results,
        formId,
      }

      // Determine if email was captured (email field exists and is valid)
      const emailCaptured = Boolean(
        results.email && typeof results.email === 'string' && results.email.length > 0,
      )

      // Save to localStorage
      const payload = {
        leadForm: sanitizeData(resultsWithFormId, LEAD_FORM_SENSITIVE_FIELDS),
        enrichCustomerResponse: sanitizeData(
          enrichCustomerResponse,
          ENRICH_CUSTOMER_SENSITIVE_FIELDS,
        ),
        consumptionPredictionResponse: sanitizeData(
          consumptionPredictionResponse,
          LEAD_FORM_SENSITIVE_FIELDS,
        ),
        quizCompletionPercentage: finalProgress,
        emailCaptured,
      }
      saveToLocalStorage('fmpContext', payload)

      // Log for non-production environments only
      if (environment !== 'production') {
        console.log('Form completed with results:', results)
        console.log('Quiz completion percentage:', finalProgress)
        console.log('Email captured:', emailCaptured)
      }
    }),

    // Set current results to fmpContext in localStorage
    handleErrorCta: thunk((actions, _, { getStoreState, getState }) => {
      const storeState = getStoreState()
      const questionnaireState = getState()
      const storagePayload = {
        leadForm: sanitizeData(questionnaireState.formResults, LEAD_FORM_SENSITIVE_FIELDS),
        enrichCustomerResponse: sanitizeData(
          storeState.enrichCustomer.enrichCustomerResponse,
          ENRICH_CUSTOMER_SENSITIVE_FIELDS,
        ),
        consumptionPredictionResponse: sanitizeData(
          storeState.consumptionPrediction.consumptionPredictionResponse,
          LEAD_FORM_SENSITIVE_FIELDS,
        ),
        quizCompletionPercentage: questionnaireState.quizCompletionPercentage,
      }

      saveToLocalStorage('fmpContext', storagePayload)
    }),

    handleNextStep: thunk((actions, { stepName, results }, { getState }) => {
      // Submitting the interstitial step commits the user to a flow (quick or
      // deepDive). The two flows have different active core counts → different
      // per-step percentages, so any high-water mark from a prior flow would
      // wrongly pin the bar above the new flow's first values. Reset before
      // the next step's calculation runs.
      if (stepName === 'interstitialRadioGroup') {
        actions.resetCheckpointProgress()
      }

      actions.setFormResults(results)
      actions.setLastCompletedStepName(stepName)

      const currentState = getState()
      const currentIndex = currentState.currentStepIndex
      const questions = currentState.filteredQuestions
      const formResults = currentState.formResults

      // Find the next VISIBLE step by scanning forward from current position
      // This ensures navigation matches what Odyssey will actually render
      let nextIndex = currentIndex + 1

      while (nextIndex < questions.length) {
        const candidateStep = questions[nextIndex]

        const isVisible =
          !candidateStep?.visibleIf ||
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          isStepVisible(candidateStep.visibleIf as any, formResults)

        if (isVisible) {
          break // Found the next visible step
        }

        nextIndex++ // Skip invisible step
      }

      actions.setCurrentStepIndex(nextIndex)

      if (nextIndex < questions.length) {
        const nextStep = questions[nextIndex]
        actions.setCurrentStep(nextStep || null)
      }

      // checkpointProgress is updated by setCurrentStep above — read it directly
      // so analytics always matches what the user saw on screen.
      actions.setQuizCompletionPercentage(getState().checkpointProgress)
    }),

    handlePrevStep: thunk((actions, { results }, { getState }) => {
      actions.setFormResults(results)
      const currentState = getState()
      const currentIndex = currentState.currentStepIndex
      if (currentIndex <= 0) return

      const questions = currentState.filteredQuestions
      const formResults = currentState.formResults

      // Scan backward through filteredQuestions to find the nearest step that Odyssey
      // would actually render. Odyssey skips steps whose visibleIf conditions are not
      // met, so we must do the same — otherwise we hand Odyssey a hidden-step index
      // and it may navigate in the wrong direction or show the wrong step entirely.
      //
      // Steps gated by *other* steps' answers (e.g. `homeInfo` is gated by
      // `address.enriched`) are correctly skipped. The "hide once answered" pattern
      // (a step whose `visibleIf` references its own form key) is intentionally NOT
      // supported here — see the regression test in `formModel.test.ts`
      // for the rationale. If every prior step is hidden we bail out rather than
      // landing on an invisible step.
      let prevIndex = currentIndex - 1
      while (prevIndex >= 0) {
        const step = questions[prevIndex]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (!step?.visibleIf || isStepVisible(step.visibleIf as any, formResults)) break
        prevIndex--
      }
      if (prevIndex < 0) return

      actions.setCurrentStepIndex(prevIndex)

      if (prevIndex < questions.length) {
        actions.setCurrentStep(questions[prevIndex] || null)
      }

      // checkpointProgress is updated by setCurrentStep above — read it directly
      // so analytics always matches what the user saw on screen.
      actions.setQuizCompletionPercentage(getState().checkpointProgress)
    }),
  }
}
